import { z } from 'zod';
import { BucketSchema, IsoDateTimeSchema, SeveritySchema, UuidSchema } from './common.js';

export const AuditStatusSchema = z.enum([
  'queued',
  'discovering',
  'crawling',
  'checking',
  'analyzing',
  'enriching',
  'scoring',
  'narrating',
  'rendering',
  'needs_review',
  'completed',
  'failed',
  'cancelled',
]);
export type AuditStatus = z.infer<typeof AuditStatusSchema>;

/** Progress payload the workflow writes into `audits.progress`. */
export const AuditProgressSchema = z.object({
  phase: z.string(),
  pct: z.number().min(0).max(100).optional(),
  note: z.string().optional(),
  pagesDiscovered: z.number().int().min(0).optional(),
  pagesCrawled: z.number().int().min(0).optional(),
  pagesFailed: z.number().int().min(0).optional(),
  issuesFound: z.number().int().min(0).optional(),
  updatedAt: IsoDateTimeSchema.optional(),
});
export type AuditProgress = z.infer<typeof AuditProgressSchema>;

export const AuditScopeSchema = z.object({
  max_pages: z.number().int().min(1).max(5000).default(200),
  max_depth: z.number().int().min(1).max(8).default(5),
  include_patterns: z.array(z.string()).max(50).optional(),
  exclude_patterns: z.array(z.string()).max(50).optional(),
  render_js: z.enum(['auto', 'always', 'never']).default('auto'),
  psi_sample_size: z.number().int().min(0).max(30).default(10),
  /** v2 crawler tuning. */
  per_host_concurrency: z.number().int().min(1).max(10).default(3),
  min_delay_ms_per_host: z.number().int().min(0).max(10_000).default(500),
  follow_subdomains: z.boolean().default(false),
  respect_robots: z.boolean().default(true),
  strip_param_patterns: z.array(z.string()).max(50).optional(),
  scoring_profile_id: z.string().default('default'),
  /**
   * @deprecated Phase 0 — HITL `needs_review` gate removed. Accepted for
   * backwards-compatible request bodies but ignored by createAudit.
   */
  needs_review: z.boolean().default(false),
});
export type AuditScope = z.infer<typeof AuditScopeSchema>;

export const SubScoresSchema = z.object({
  technical: z.number().int().min(0).max(100),
  on_page: z.number().int().min(0).max(100),
  performance: z.number().int().min(0).max(100),
  content: z.number().int().min(0).max(100),
  indexing: z.number().int().min(0).max(100),
});
export type SubScores = z.infer<typeof SubScoresSchema>;

/**
 * `severity` is optional so legacy audits (pre-v2) that stored plain warnings
 * still parse. New writes set 'info' for non-blocking notes (audit still valid)
 * and 'warning' for concerns the user should look at.
 */
export const AuditWarningSchema = z.object({
  code: z.string(),
  severity: z.enum(['info', 'warning']).optional(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});
export type AuditWarning = z.infer<typeof AuditWarningSchema>;

export const AuditSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: AuditStatusSchema,
  scope: AuditScopeSchema,
  health_score: z.number().int().min(0).max(100).nullable(),
  sub_scores: SubScoresSchema.nullable(),
  content_score: z.number().int().min(0).max(100).nullable().optional(),
  pages_discovered: z.number().int().min(0).nullable().optional(),
  pages_crawled: z.number().int().min(0).nullable().optional(),
  pages_failed: z.number().int().min(0).nullable().optional(),
  issues_count: z.number().int().min(0).nullable().optional(),
  scoring_profile_id: z.string().optional(),
  progress: AuditProgressSchema.optional(),
  warnings: z.array(AuditWarningSchema),
  started_at: IsoDateTimeSchema.nullable(),
  completed_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type Audit = z.infer<typeof AuditSchema>;

/** v2 issue schema — the API-facing shape returned by `/v1/audits/:id/issues`. */
export const AuditIssueSchema = z.object({
  id: UuidSchema,
  audit_id: UuidSchema,
  check_id: z.string(),
  severity: SeveritySchema,
  bucket: BucketSchema,
  weight: z.number(),
  title: z.string(),
  summary: z.string(),
  evidence: z.record(z.unknown()),
  affected_url_count: z.number().int().min(0),
  sample_urls: z.array(z.string()),
  llm_explanation: z.string().nullable(),
  llm_fix: z.string().nullable(),
  llm_fix_code: z.string().nullable(),
  llm_fix_lang: z.string().nullable(),
  impact_score: z.number().int().min(0).max(100).nullable(),
  ease_score: z.number().int().min(0).max(100).nullable(),
  created_at: IsoDateTimeSchema,
});
export type AuditIssue = z.infer<typeof AuditIssueSchema>;

export const AuditIssueAffectedUrlSchema = z.object({
  issue_id: UuidSchema,
  audit_id: UuidSchema,
  url: z.string(),
  page_id: UuidSchema.nullable(),
  evidence: z.record(z.unknown()).nullable(),
});
export type AuditIssueAffectedUrl = z.infer<typeof AuditIssueAffectedUrlSchema>;

export const AuditPageSummarySchema = z.object({
  id: UuidSchema,
  url: z.string(),
  title: z.string().nullable(),
  status_code: z.number().int().nullable(),
  status_class: z.number().int().nullable(),
  depth: z.number().int().nullable(),
  discovered_from: z.string().nullable(),
  word_count: z.number().int().nullable(),
  inbound_link_count: z.number().int().nullable(),
  outbound_internal_link_count: z.number().int().nullable(),
  critical_count: z.number().int().min(0),
  high_count: z.number().int().min(0),
  medium_count: z.number().int().min(0),
  low_count: z.number().int().min(0),
  content_quality_score: z.number().int().min(0).max(100).nullable(),
  performance_score: z.number().int().min(0).max(100).nullable(),
});
export type AuditPageSummary = z.infer<typeof AuditPageSummarySchema>;

export const CreateAuditRequestSchema = z.object({
  project_id: UuidSchema,
  scope: AuditScopeSchema.partial().optional(),
});
export type CreateAuditRequest = z.infer<typeof CreateAuditRequestSchema>;

export const FindingSchema = z.object({
  id: UuidSchema,
  audit_id: UuidSchema,
  check_id: z.string(),
  severity: SeveritySchema,
  bucket: BucketSchema,
  affected_url: z.string().nullable(),
  evidence: z.record(z.unknown()),
  recommendation: z.string().nullable(),
  impact_score: z.number().int().min(0).max(100).nullable(),
  ease_score: z.number().int().min(0).max(100).nullable(),
  created_at: IsoDateTimeSchema,
});
export type Finding = z.infer<typeof FindingSchema>;

/** Output schema for the LLM narration step (audit). */
export const NarrationSchema = z.object({
  executive_summary: z.string().min(200).max(2000),
  per_finding: z.record(
    z.object({
      explanation: z.string().min(20).max(800),
      fix: z.string().min(20).max(800),
    }),
  ),
});
export type Narration = z.infer<typeof NarrationSchema>;
